import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { gerarPDFEditor } from '../services/api';
import ProductOperations from '../components/ListarPecas/ProductOperations';
import ProductAssemblyPreview from '../components/ProductAssemblyPreview/ProductAssemblyPreview';
import ProductStepPendings from '../components/ProductStepPendings/ProductStepPendings';
import ProductStepPreviewModal from '../components/ProductStepPreview/ProductStepPreviewModal';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Skeleton from '../components/ui/Skeleton';
import { Table, TableCode } from '../components/ui/Table';
import { downloadBlob } from '../utils/downloadBlob';
import './ProductPage.css';

const PRODUCT_LOADING = { status: 'loading', data: null, error: null };
const PARTS_IDLE = { status: 'idle', data: [], error: null };
const PARTS_TABLE_WIDTHS_KEY = 'corewood.product.partsTable.columnWidths.v1';
const PARTS_TABLE_COLUMNS = [
  { key: 'codigo', label: 'Código', width: 90, minWidth: 60 },
  { key: 'descricao', label: 'Descrição', width: 260, minWidth: 120 },
  { key: 'quantidade', label: 'Qtd.', width: 65, minWidth: 50 },
  { key: 'comprimento', label: 'Compr.', width: 90, minWidth: 65 },
  { key: 'largura', label: 'Larg.', width: 90, minWidth: 65 },
  { key: 'material', label: 'Material', width: 90, minWidth: 65 },
  { key: 'espessura', label: 'Esp.', width: 65, minWidth: 50 },
];

function defaultPartsTableWidths() {
  return Object.fromEntries(PARTS_TABLE_COLUMNS.map(({ key, width }) => [key, width]));
}

function readPartsTableWidths() {
  const defaults = defaultPartsTableWidths();
  try {
    const saved = JSON.parse(window.localStorage.getItem(PARTS_TABLE_WIDTHS_KEY));
    if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return defaults;
    return Object.fromEntries(PARTS_TABLE_COLUMNS.map(({ key, minWidth }) => {
      const width = saved[key];
      return [key, typeof width === 'number' && Number.isFinite(width)
        && width >= minWidth && width <= 2000 ? width : defaults[key]];
    }));
  } catch (error) {
    return defaults;
  }
}

function abbreviatedMaterial(value) {
  if (!value) return '—';
  if (/^MDF/i.test(value)) return 'MDF';
  if (/^(?:MDP|AGL)/i.test(value)) return 'MDP';
  return value;
}

const numberFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});

function formatNumber(value, suffix = '') {
  if (value == null || value === '') return '—';
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return `${numberFormatter.format(number)}${suffix}`;
}

function formatBoolean(value) {
  if (value == null) return null;
  return value ? 'Sim' : 'Não';
}

function countItems(value) {
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === 'object') {
    return Object.values(value).reduce(
      (total, items) => total + (Array.isArray(items) ? items.length : 0),
      0
    );
  }
  return 0;
}

function detailEntry(label, value, className) {
  return value == null || value === '' ? null : [label, value, className];
}

function formatBordas(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const labels = { topo: 'Superior', baixo: 'Inferior', esquerda: 'Esquerda', direita: 'Direita',
    top: 'Superior', bottom: 'Inferior', left: 'Esquerda', right: 'Direita' };
  return Object.entries(value)
    .filter(([, tipo]) => tipo && tipo !== 'nenhum')
    .map(([lado, tipo]) => [labels[lado] || lado, tipo]);
}

function ProductHeader({ product, onBack }) {
  return (
    <Card className="cw-product-header">
      <div className="cw-product-header-top">
        <p className="cw-product-header-eyebrow">Produto</p>
        <Button variant="secondary" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" />
          Voltar para Produtos
        </Button>
      </div>
      <div className="cw-product-header-main">
        <h1 className="cw-page-title">{product.nome || 'Produto sem nome'}</h1>
        <Badge variant={product.is_provisional ? 'warning' : 'success'} dot>
          {product.is_provisional ? 'Provisório' : 'Formal'}
        </Badge>
      </div>
      <div className="cw-product-header-metadata" aria-label="Metadados do produto">
        <span className="cw-product-header-metadata-item">
          <span className="cw-product-header-metadata-label">Código</span>
          <TableCode>{product.codigo}</TableCode>
        </span>
        <span className="cw-product-header-id">ID interno {product.id}</span>
      </div>
    </Card>
  );
}

function ProductLoading() {
  return (
    <div className="cw-product-page-loading" aria-label="Carregando produto">
      <Card>
        <Skeleton width="140px" height="12px" style={{ marginBottom: '12px' }} />
        <Skeleton width="42%" height="30px" style={{ marginBottom: '12px' }} />
        <Skeleton width="180px" height="20px" />
      </Card>
      <div className="cw-product-content-grid">
        <Card>
          <Skeleton width="120px" height="18px" style={{ marginBottom: '20px' }} />
          <PartsSkeleton />
        </Card>
        <Card>
          <Skeleton width="150px" height="18px" style={{ marginBottom: '20px' }} />
          <PartsSkeleton rows={4} />
        </Card>
      </div>
    </div>
  );
}

function PartsSkeleton({ rows = 5 }) {
  return (
    <div className="cw-product-parts-skeleton" aria-label="Carregando peças">
      {Array.from({ length: rows }, (_, index) => (
        <div className="cw-product-parts-skeleton-row" key={index}>
          <Skeleton height="14px" />
          <Skeleton height="14px" />
          <Skeleton height="14px" />
        </div>
      ))}
    </div>
  );
}

function PartsTable({ state, selectedId, onSelect }) {
  const [columnWidths, setColumnWidths] = useState(readPartsTableWidths);
  const widthsRef = useRef(columnWidths);
  const stopResizeRef = useRef(null);
  const tableWidth = PARTS_TABLE_COLUMNS.reduce(
    (total, { key }) => total + columnWidths[key], 0
  );

  useEffect(() => () => {
    if (stopResizeRef.current) stopResizeRef.current();
  }, []);

  function startResize(event, column) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    if (stopResizeRef.current) stopResizeRef.current();

    const startX = event.clientX;
    const startWidth = widthsRef.current[column.key];
    const pointerId = event.pointerId;
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const matchesPointer = (moveEvent) => moveEvent.pointerId === pointerId;
    const onMove = (moveEvent) => {
      if (!matchesPointer(moveEvent)) return;
      const width = Math.max(column.minWidth, Math.round(
        startWidth + moveEvent.clientX - startX
      ));
      if (width === widthsRef.current[column.key]) return;
      widthsRef.current = { ...widthsRef.current, [column.key]: width };
      setColumnWidths(widthsRef.current);
    };
    const stop = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      window.removeEventListener('blur', onEnd);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
      stopResizeRef.current = null;
    };
    const onEnd = (endEvent) => {
      if (endEvent.pointerId != null && !matchesPointer(endEvent)) return;
      stop();
      try {
        window.localStorage.setItem(
          PARTS_TABLE_WIDTHS_KEY, JSON.stringify(widthsRef.current)
        );
      } catch (error) {
        // A largura em memória continua utilizável quando o storage está indisponível.
      }
    };
    stopResizeRef.current = stop;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    window.addEventListener('blur', onEnd);
  }

  function restoreColumns() {
    if (stopResizeRef.current) stopResizeRef.current();
    const defaults = defaultPartsTableWidths();
    widthsRef.current = defaults;
    setColumnWidths(defaults);
    try {
      window.localStorage.removeItem(PARTS_TABLE_WIDTHS_KEY);
    } catch (error) {
      // O reset visual funciona mesmo sem acesso ao storage.
    }
  }

  return (
    <Card className="cw-product-parts-card">
      <div className="cw-product-section-header">
        <h2 className="cw-section-title">Peças</h2>
        <div className="cw-product-parts-header-actions">
          {state.status === 'success' && (
            <span className="cw-product-section-count">
              {state.data.length} {state.data.length === 1 ? 'peça' : 'peças'}
            </span>
          )}
          {state.status === 'success' && state.data.length > 0 && (
            <Button variant="secondary" className="cw-product-parts-reset" onClick={restoreColumns}>
              Restaurar colunas
            </Button>
          )}
        </div>
      </div>

      {state.status === 'loading' && <PartsSkeleton />}

      {state.status === 'error' && (
        <div className="cw-product-parts-error" role="alert">{state.error}</div>
      )}

      {state.status === 'success' && state.data.length === 0 && (
        <p className="cw-product-parts-empty">
          Este produto ainda não possui peças no workspace operacional.
        </p>
      )}

      {state.status === 'success' && state.data.length > 0 && (
        <Table className="cw-product-parts-table" style={{ minWidth: `${tableWidth}px` }}>
          <colgroup>
            {PARTS_TABLE_COLUMNS.map(({ key }) => (
              <col key={key} style={{ width: `${columnWidths[key]}px` }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {PARTS_TABLE_COLUMNS.map((column) => (
                <th key={column.key} scope="col">
                  {column.label}
                  <span
                    className="cw-product-column-resize-handle"
                    role="separator"
                    aria-label={`Redimensionar coluna ${column.label}`}
                    aria-orientation="vertical"
                    aria-valuemin={column.minWidth}
                    aria-valuenow={columnWidths[column.key]}
                    onPointerDown={(event) => startResize(event, column)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {state.data.map((piece) => {
              const selected = piece.id === selectedId;
              const select = () => onSelect(piece);
              return (
                <tr
                  key={piece.id}
                  className={`cw-product-parts-row${selected ? ' cw-product-parts-row--selected' : ''}`}
                  aria-selected={selected}
                  tabIndex="0"
                  onClick={select}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      select();
                    }
                  }}
                >
                  <td><TableCode>{piece.codigo}</TableCode></td>
                  <td title={piece.nome || undefined}>{piece.nome || '—'}</td>
                  <td>{formatNumber(piece.quantidade)}</td>
                  <td><TableCode>{formatNumber(piece.comprimento, ' mm')}</TableCode></td>
                  <td><TableCode>{formatNumber(piece.largura, ' mm')}</TableCode></td>
                  <td title={piece.material || undefined}>{abbreviatedMaterial(piece.material)}</td>
                  <td><TableCode>{formatNumber(piece.espessura, ' mm')}</TableCode></td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function DetailList({ entries }) {
  return (
    <dl className="cw-product-details-list">
      {entries.map(([label, value, className]) => (
        <React.Fragment key={label}>
          <dt>{label}</dt>
          <dd className={className || ''}>{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function PartDetailsPanel({
  state,
  selectedPiece,
  onEditPiece,
  onGeneratePdf,
  onImportStep,
  isGeneratingPdf,
  stepPendingsRevision,
}) {
  const general = useMemo(() => {
    if (!selectedPiece) return [];
    const { codigo, nome, quantidade, material, espessura, comprimento, largura } = selectedPiece;
    return [
      detailEntry('Código', codigo, 'cw-product-detail-code'),
      detailEntry('Descrição', nome),
      detailEntry('Quantidade', quantidade == null ? null : formatNumber(quantidade)),
      detailEntry('Material', material ? abbreviatedMaterial(material).slice(0, 3) : null),
      detailEntry('Espessura', espessura == null ? null : formatNumber(espessura, ' mm')),
      detailEntry('Dimensões', comprimento == null || largura == null
        ? null : `${formatNumber(comprimento)} × ${formatNumber(largura)} mm`,
      'cw-product-detail-code'),
    ].filter(Boolean);
  }, [selectedPiece]);

  const classification = useMemo(() => {
    if (!selectedPiece) return [];
    return [
      detailEntry('Versão', selectedPiece.versao),
      detailEntry('Alerta', formatBoolean(selectedPiece.alerta)),
    ].filter(Boolean);
  }, [selectedPiece]);

  const processes = useMemo(() => {
    if (!selectedPiece) return [];
    const furos = countItems(selectedPiece.furos);
    const rasgos = countItems(selectedPiece.rasgos);
    const bordas = formatBordas(selectedPiece.bordas);
    const regras = selectedPiece.regras_especiais?.length || 0;
    return [
      detailEntry('Furos', furos > 0 ? formatNumber(furos) : null),
      detailEntry('Rasgos', rasgos > 0 ? formatNumber(rasgos) : null),
      detailEntry('Bordas', bordas.length > 0
        ? `${bordas.length} ${bordas.length === 1 ? 'lado' : 'lados'}` : null),
      ...bordas,
      detailEntry('Regras especiais', regras > 0 ? formatNumber(regras) : null),
    ].filter(Boolean);
  }, [selectedPiece]);

  return (
    <Card className="cw-product-details-card">
      <div className="cw-product-section-header">
        <h2 className="cw-section-title">Detalhes da peça</h2>
      </div>

      {state.status === 'loading' && <PartsSkeleton rows={5} />}

      {state.status === 'error' && (
        <div className="cw-product-parts-error" role="alert">
          Os detalhes não puderam ser carregados porque a lista de peças falhou.
        </div>
      )}

      {state.status === 'success' && !selectedPiece && (
        <p className="cw-product-details-empty">
          Selecione uma peça na tabela para consultar os detalhes.
        </p>
      )}

      {state.status === 'success' && selectedPiece && (
        <div className="cw-product-details">
          {general.length > 0 && (
            <section className="cw-product-details-section">
              <h3 className="cw-product-details-section-title">Geral</h3>
              <DetailList entries={general} />
            </section>
          )}

          {classification.length > 0 && (
            <section className="cw-product-details-section">
              <h3 className="cw-product-details-section-title">Classificação</h3>
              <DetailList entries={classification} />
            </section>
          )}

          {processes.length > 0 && (
            <section className="cw-product-details-section">
              <h3 className="cw-product-details-section-title">Processos</h3>
              <DetailList entries={processes} />
            </section>
          )}

          <div className="cw-product-details-section">
            <h3 className="cw-product-details-section-title">Ações</h3>
            <div className="cw-product-piece-actions">
              <Button variant="secondary" onClick={() => onEditPiece(selectedPiece)}>
                Editar no MPR
              </Button>
              <Button
                variant="secondary"
                loading={isGeneratingPdf}
                onClick={() => onGeneratePdf(selectedPiece)}
              >
                Gerar PDF
              </Button>
              <Button variant="secondary" onClick={() => onImportStep(selectedPiece)}>
                Importar STEP
              </Button>
            </div>
          </div>
        </div>
      )}

      {state.status === 'success' && selectedPiece && (
        <ProductStepPendings
          key={`${selectedPiece.id}:${stepPendingsRevision}`}
          pecaId={selectedPiece.id}
        />
      )}
    </Card>
  );
}

function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [productState, setProductState] = useState(PRODUCT_LOADING);
  const [partsState, setPartsState] = useState(PARTS_IDLE);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [stepPreviewPiece, setStepPreviewPiece] = useState(null);
  const [stepPendingsRevision, setStepPendingsRevision] = useState(0);
  const pdfRequestsRef = useRef(new Set());
  const [generatingPdfPieceIds, setGeneratingPdfPieceIds] = useState([]);

  useEffect(() => {
    const parsedId = Number(productId);
    if (!Number.isInteger(parsedId) || parsedId <= 0) {
      setProductState({
        status: 'error',
        data: null,
        error: 'O identificador do produto é inválido.',
      });
      setPartsState(PARTS_IDLE);
      setSelectedPiece(null);
      return undefined;
    }

    let active = true;
    let requestPhase = 'product';
    setProductState(PRODUCT_LOADING);
    setPartsState(PARTS_IDLE);
    setSelectedPiece(null);

    api.get(`/api/produtos/${parsedId}`)
      .then((response) => {
        if (!active) return null;
        const product = response.data;
        setProductState({ status: 'success', data: product, error: null });
        requestPhase = 'parts';
        setPartsState({ status: 'loading', data: [], error: null });
        return api.get(`/pecas/produto/${encodeURIComponent(product.codigo)}`);
      })
      .then((response) => {
        if (!active || response == null) return;
        if (!Array.isArray(response.data)) {
          throw new Error('Resposta inválida ao listar peças do Product.');
        }
        setPartsState({ status: 'success', data: response.data, error: null });
      })
      .catch((error) => {
        if (!active) return;
        if (requestPhase === 'product' && error.response?.status === 404) {
          setProductState({
            status: 'not-found',
            data: null,
            error: 'Produto não encontrado.',
          });
          setPartsState(PARTS_IDLE);
          return;
        }
        if (requestPhase === 'product') {
          setProductState({
            status: 'error',
            data: null,
            error: 'Não foi possível carregar o produto.',
          });
          setPartsState(PARTS_IDLE);
          return;
        }
        console.error('ProductPage: falha ao carregar peças:', error);
        setPartsState({
          status: 'error',
          data: [],
          error: 'Não foi possível carregar as peças deste produto.',
        });
      });

    return () => {
      active = false;
    };
  }, [productId]);

  if (productState.status === 'loading') {
    return <ProductLoading />;
  }

  if (productState.status === 'error' || productState.status === 'not-found') {
    return (
      <Card className="cw-product-page-error" role="alert">
        <h1 className="cw-page-title">
          {productState.status === 'not-found' ? 'Produto não encontrado' : 'Falha ao abrir produto'}
        </h1>
        <p className="cw-product-page-error-message">{productState.error}</p>
        <Button variant="secondary" onClick={() => navigate('/pecas')}>
          <ArrowLeft size={16} aria-hidden="true" />
          Voltar para Produtos
        </Button>
      </Card>
    );
  }

  const product = productState.data;
  const refreshParts = () => {
    setPartsState({ status: 'loading', data: [], error: null });
    api.get(`/pecas/produto/${encodeURIComponent(product.codigo)}`)
      .then((response) => {
        if (!Array.isArray(response.data)) {
          throw new Error('Resposta inválida ao listar peças do Product.');
        }
        setPartsState({ status: 'success', data: response.data, error: null });
        setSelectedPiece((current) => response.data.find((piece) => piece.id === current?.id) || null);
      })
      .catch((error) => {
        console.error('ProductPage: falha ao recarregar peças:', error);
        setPartsState({ status: 'error', data: [], error: 'Não foi possível carregar as peças deste produto.' });
      });
  };

  const handleStepApplied = () => {
    setStepPendingsRevision((current) => current + 1);
    refreshParts();
  };

  const generateSelectedPiecePdf = async (piece) => {
    if (!piece || pdfRequestsRef.current.has(piece.id)) return;

    pdfRequestsRef.current.add(piece.id);
    setGeneratingPdfPieceIds((current) => [...current, piece.id]);
    try {
      const verticalHoles = Array.isArray(piece.furos)
        ? piece.furos
        : piece.furos?.verticais || [];
      const horizontalHoles = Array.isArray(piece.furosHorizontais)
        ? piece.furosHorizontais
        : piece.furos?.horizontais || [];
      const { blob, total: warningCount } = await gerarPDFEditor({
        ...piece,
        peca_id: piece.id,
        furos: verticalHoles,
        furosHorizontais: horizontalHoles,
      });

      downloadBlob(blob, `${piece.nome || piece.codigo || 'peca'}_furacao.pdf`);
      if (warningCount > 0) {
        window.alert(`PDF gerado com ${warningCount} aviso(s) de furação. Confira o desenho.`);
      } else {
        window.alert('PDF gerado com sucesso!');
      }
    } catch (error) {
      console.error('ProductPage: falha ao gerar PDF individual:', error);
      let message = error.response?.data?.detail || error.message;
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          message = JSON.parse(text).detail || message;
        } catch (parseError) {
          console.error('ProductPage: falha ao interpretar erro do PDF individual:', parseError);
        }
      }
      window.alert(`Erro ao gerar PDF: ${message || 'falha desconhecida'}`);
    } finally {
      pdfRequestsRef.current.delete(piece.id);
      setGeneratingPdfPieceIds((current) => current.filter((id) => id !== piece.id));
    }
  };

  return (
    <div className="cw-product-page">
      <ProductHeader product={product} onBack={() => navigate('/pecas')} />

      {partsState.status === 'success' && (
        <ProductOperations
          key={product.id}
          pecas={partsState.data}
          codigoProduto={product.codigo}
          productId={product.id}
          onPartsUpdated={refreshParts}
        />
      )}

      {partsState.status === 'success' && (
        <ProductAssemblyPreview
          productId={product.id}
          pieces={partsState.data}
        />
      )}

      <div className="cw-product-content-grid">
        <PartsTable
          state={partsState}
          selectedId={selectedPiece?.id ?? null}
          onSelect={setSelectedPiece}
        />
        <PartDetailsPanel
          state={partsState}
          selectedPiece={selectedPiece}
          onEditPiece={(piece) => navigate(`/editor?product_id=${product.id}`, { state: { peca: piece } })}
          onGeneratePdf={generateSelectedPiecePdf}
          onImportStep={setStepPreviewPiece}
          isGeneratingPdf={generatingPdfPieceIds.includes(selectedPiece?.id)}
          stepPendingsRevision={stepPendingsRevision}
        />
      </div>

      {stepPreviewPiece && (
        <ProductStepPreviewModal
          piece={stepPreviewPiece}
          onClose={() => setStepPreviewPiece(null)}
          onApplied={handleStepApplied}
        />
      )}
    </div>
  );
}

export default ProductPage;
